import { INTL_DERIVED_KEYS, intlCalendarNames, intlLongDate, safeFormatter } from './intl.js';
import { DEFAULT_LOCALE, getDisplayDigits, localizeDigits, normalizeLocale, } from './locales.js';
import { mergeCatalog, pluralLocaleOf } from './merge.js';
import { nl } from './nl.js';
import { pluralCategory, pluralForm } from './plural.js';
/**
 * Vervangt `{naam}`-placeholders. Onbekende placeholders blijven staan (zichtbaar in QA). Getallen
 * volgen de cijferkeuze van deze app (Arabisch-Indisch alleen als het gezin dat koos).
 */
export function interpolate(template, params) {
    if (!params)
        return template;
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        const value = params[key];
        if (value === undefined)
            return match;
        return typeof value === 'number' ? localizeDigits(String(value), getDisplayDigits()) : value;
    });
}
/**
 * t(): kiest bij een meervoudsobject de vorm op basis van `params.count` en de meervoudsregels van
 * de taal waar de tekst vandaan komt (een Arabische tekst met Arabische regels; een Nederlandse
 * terugval met de Nederlandse), en interpoleert. `locale` overschrijft dat (servergebruik).
 */
export function t(message, params, locale) {
    var _a;
    if (typeof message === 'string')
        return interpolate(message, params);
    const count = params === null || params === void 0 ? void 0 : params.count;
    const n = typeof count === 'number' ? count : typeof count === 'string' ? Number(count) : NaN;
    const lang = (_a = locale !== null && locale !== void 0 ? locale : pluralLocaleOf(message)) !== null && _a !== void 0 ? _a : DEFAULT_LOCALE;
    const form = isNaN(n) ? message.other : pluralForm(message, pluralCategory(lang, n));
    return interpolate(form, params);
}
/**
 * Als t(), maar in stukken: vaste tekst en ingevulde waarden apart (review g3-ux r1). Zo kan een
 * scherm elke waarde in zijn eigen richting zetten (<bdi>): een Latijnse naam vooraan een Arabische
 * zin maakt dan niet de hele zin links-naar-rechts.
 */
export function tParts(message, params) {
    var _a;
    const count = params.count;
    const n = typeof count === 'number' ? count : typeof count === 'string' ? Number(count) : NaN;
    const template = typeof message === 'string'
        ? message
        : isNaN(n)
            ? message.other
            : pluralForm(message, pluralCategory((_a = pluralLocaleOf(message)) !== null && _a !== void 0 ? _a : DEFAULT_LOCALE, n));
    const out = [];
    const re = /\{(\w+)\}/g;
    let last = 0;
    let m;
    while ((m = re.exec(template)) !== null) {
        const key = m[1];
        const value = params[key];
        if (value === undefined)
            continue;
        if (m.index > last)
            out.push(template.slice(last, m.index));
        out.push({
            key,
            value: typeof value === 'number' ? localizeDigits(String(value), getDisplayDigits()) : value,
        });
        last = m.index + m[0].length;
    }
    if (last < template.length)
        out.push(template.slice(last));
    return out;
}
/**
 * Bord-catalogus voor een taal uit nl + een (gedeeltelijk) taalbestand. Weekdagen en maanden die
 * het taalbestand niet heeft, komen uit Intl (beter dan Nederlandse namen in een Turkse zin); kent
 * Intl de taal niet, dan blijven de Nederlandse staan. Kiosk-veilig: geen andere talen hier.
 */
export function buildMessages(locale, partial) {
    const code = normalizeLocale(locale);
    if (code === DEFAULT_LOCALE)
        return nl;
    const merged = mergeCatalog(nl, partial, code);
    const given = (partial !== null && partial !== void 0 ? partial : {});
    const names = code === 'en-GB' ? null : intlCalendarNames(code);
    const out = { ...merged, locale: code };
    if (names) {
        for (const key of INTL_DERIVED_KEYS) {
            const own = given[key];
            if (!Array.isArray(own) || own.length !== nl[key].length) {
                out[key] = names[key];
            }
        }
    }
    return out;
}
/** Datum als "woensdag 23 september"; andere talen via Intl in hun eigen volgorde. */
export function formatLongDate(m, date) {
    var _a, _b;
    const code = normalizeLocale(m.locale);
    if (code !== 'nl-NL' && code !== 'en-GB') {
        const viaIntl = intlLongDate(code, date, getDisplayDigits());
        if (viaIntl)
            return viaIntl;
    }
    const weekday = (_a = m.weekdays[(weekday7to0(date.weekday) + 7) % 7]) !== null && _a !== void 0 ? _a : '';
    const month = (_b = m.months[date.month - 1]) !== null && _b !== void 0 ? _b : '';
    return localizeDigits(`${weekday} ${date.day} ${month}`, getDisplayDigits());
}
const DATE_OPTIONS = {
    long: { weekday: 'long', day: 'numeric', month: 'long' },
    short: { weekday: 'short', day: 'numeric', month: 'short' },
    day: { day: 'numeric', month: 'long' },
    weekdayDay: { weekday: 'short', day: 'numeric' },
};
/**
 * Een kalenderdatum als tekst in de taal van de catalogus: nl en en met de eigen namen ("woensdag
 * 23 september", "wo 23 sep", "23 september", "wo 23"), andere talen via Intl in hun eigen volgorde
 * ("23 Eylül Çarşamba"). Cijfers volgen de keuze van deze app. `withYear`: jaartal erachter.
 */
export function formatDateParts(m, date, style, withYear = false) {
    var _a, _b, _c, _d, _e, _f;
    const code = normalizeLocale(m.locale);
    const digits = getDisplayDigits();
    if (code !== 'nl-NL' && code !== 'en-GB') {
        const f = safeFormatter(code, { ...DATE_OPTIONS[style], ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' }, digits);
        if (f) {
            try {
                return f.format(new Date(Date.UTC(date.year, date.month - 1, date.day, 12)));
            }
            catch {
                // terugval hieronder
            }
        }
    }
    const wd = (new Date(Date.UTC(date.year, date.month - 1, date.day, 12)).getUTCDay() + 6) % 7;
    const year = withYear ? ` ${date.year}` : '';
    let text;
    switch (style) {
        case 'long':
            text = `${(_a = m.weekdays[wd]) !== null && _a !== void 0 ? _a : ''} ${date.day} ${(_b = m.months[date.month - 1]) !== null && _b !== void 0 ? _b : ''}${year}`;
            break;
        case 'short':
            text = `${(_c = m.weekdaysShort[wd]) !== null && _c !== void 0 ? _c : ''} ${date.day} ${(_d = m.monthsShort[date.month - 1]) !== null && _d !== void 0 ? _d : ''}${year}`;
            break;
        case 'day':
            text = `${date.day} ${(_e = m.months[date.month - 1]) !== null && _e !== void 0 ? _e : ''}${year}`;
            break;
        default:
            text = `${(_f = m.weekdaysShort[wd]) !== null && _f !== void 0 ? _f : ''} ${date.day}`;
    }
    return localizeDigits(text, digits);
}
/** ISO-weekdag (1 = maandag … 7 = zondag) → index in de weekdays-array. */
function weekday7to0(iso) {
    return iso - 1;
}
export function formatClock(hours, minutes) {
    const h = hours < 10 ? `0${hours}` : String(hours);
    const m = minutes < 10 ? `0${minutes}` : String(minutes);
    return localizeDigits(`${h}:${m}`, getDisplayDigits());
}
//# sourceMappingURL=t.js.map